/**
 * Shared scorecard step — used by both InterviewActivity and ScheduleInterview wizards.
 * Uses ScorecardTemplateBuilder from @kula-ai/components for template configuration
 * with drag-drop reordering, add/remove questions, and feedback settings.
 */
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';
import { Flex } from '@chakra-ui/react';
import {
  ScorecardTemplateBuilder,
  AddQuestion,
  type ScorecardTemplateBuilderHandle,
  type ScorecardTemplateFormValues,
  type ScorecardItem,
} from '@kula-ai/components';
import type { StepFormApi } from '@/hooks/useStepValidation';
import { ScorecardStepHeader } from './ScorecardStepHeader';
import { useScorecardTemplates } from '../ScheduleInterview/api/useScorecardTemplates';

export interface ScorecardStepCopy {
  enableLabel: string;
  aiLabel: string;
  aiDescription: string;
  templatePlaceholder: string;
  templateNote?: string;
}

export interface ScorecardStepProps {
  payloadRef: MutableRefObject<{ scorecard: Record<string, unknown> }>;
  prefillVersion?: number;
  copy: ScorecardStepCopy;
  /** Whether the account has AI scorecard fill enabled (from account settings). When false, AI toggle is hidden. */
  showAiToggle?: boolean;
}

/**
 * Maps a raw API template to ScorecardTemplateFormValues.
 */
function templateToFormValues(
  template:
    | {
        name: string;
        questions?: Array<{
          type?: string;
          question?: string;
          description?: string;
          options?: string[];
        }>;
        feedback_description?: string;
        is_feedback_mandatory?: boolean;
        rating_description?: string;
      }
    | undefined
): ScorecardTemplateFormValues {
  if (!template) {
    return {
      scoreCards: [],
      feedback_description: '',
      is_feedback_mandatory: false,
      rating_description: '',
    };
  }

  const scoreCards: ScorecardItem[] = (template.questions ?? []).map(
    (q, i) => ({
      id: String(i),
      question: q.question ?? '',
      description: q.description,
      type: (q.type as ScorecardItem['type']) ?? 'open_question',
      options: q.options?.map((o) => ({ optionText: o })),
    })
  );

  return {
    scoreCards,
    feedback_description: template.feedback_description ?? '',
    is_feedback_mandatory: Boolean(template.is_feedback_mandatory),
    rating_description: template.rating_description ?? '',
  };
}

/**
 * Maps hydrated scorecard payload back to form values.
 */
function payloadToFormValues(
  sc: Record<string, unknown>
): ScorecardTemplateFormValues {
  const questions = Array.isArray(sc.questions) ? sc.questions : [];

  return {
    scoreCards: questions.map((q: Record<string, unknown>, i: number) => ({
      id: String(q.id ?? i),
      question: (q.question as string) ?? '',
      description: q.description as string | undefined,
      type: (q.type as ScorecardItem['type']) ?? 'open_question',
      mandatory: Boolean(q.mandatory),
      options: Array.isArray(q.options)
        ? (q.options as unknown[]).map((o) =>
            typeof o === 'string'
              ? { optionText: o }
              : {
                  optionText:
                    (o as { optionText?: string })?.optionText ?? String(o),
                }
          )
        : undefined,
    })),
    feedback_description:
      (sc.feedbackDescription as string) ??
      (sc.feedback_description as string) ??
      '',
    is_feedback_mandatory: Boolean(sc.is_feedback_mandatory),
    rating_description:
      (sc.ratingDescription as string) ??
      (sc.rating_description as string) ??
      '',
  };
}

export const ScorecardStep = memo(
  forwardRef<StepFormApi, ScorecardStepProps>(
    ({ payloadRef, prefillVersion = 0, copy, showAiToggle = true }, ref) => {
      const { templates, templateOptions } = useScorecardTemplates();
      const formRef = useRef<ScorecardTemplateBuilderHandle>(null);

      const [scorecardEnabled, setScorecardEnabled] = useState(true);
      const [aiEnabled, setAiEnabled] = useState(true);
      const [selectedTemplate, setSelectedTemplate] = useState<string | null>(
        null
      );

      // Initial values for the template builder
      const [initialValues, setInitialValues] = useState<
        Partial<ScorecardTemplateFormValues>
      >(() => {
        const existing = payloadRef.current.scorecard;
        if (existing && 'questions' in existing) {
          return payloadToFormValues(existing);
        }
        return {};
      });

      // Hydrate from edit-mode payload
      useEffect(() => {
        if (prefillVersion === 0) return;
        const existing = payloadRef.current.scorecard as Record<
          string,
          unknown
        >;
        if (existing && 'questions' in existing) {
          const values = payloadToFormValues(existing);
          setInitialValues(values);
          formRef.current?.reset(values);
          setScorecardEnabled(
            (existing as { enabled?: boolean }).enabled !== false
          );
          setAiEnabled(
            (existing as { ai_enabled?: boolean }).ai_enabled !== false
          );
          setSelectedTemplate(
            (existing as { template_id?: string }).template_id ?? null
          );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [prefillVersion]);

      // Auto-select first template on create mode (skip in edit)
      const didAutoSelectTemplate = useRef(false);
      useEffect(() => {
        if (prefillVersion > 0) return; // edit mode — don't override
        if (didAutoSelectTemplate.current) return;
        if (!templates.length || selectedTemplate) return;
        didAutoSelectTemplate.current = true;
        const first = templates[0];
        setSelectedTemplate(first.name);
        const values = templateToFormValues(first);
        setInitialValues(values);
        formRef.current?.reset(values);
      }, [templates, selectedTemplate, prefillVersion]);

      // When template changes, populate questions from template data
      const handleTemplateChange = useCallback(
        (templateName: string | null) => {
          setSelectedTemplate(templateName);
          const template = templateName
            ? templates.find((t) => t.name === templateName)
            : undefined;
          const values = templateToFormValues(template);
          setInitialValues(values);
          formRef.current?.reset(values);
        },
        [templates]
      );

      useImperativeHandle(
        ref,
        () => ({
          validate: async () => {
            if (!scorecardEnabled) return true;
            if (!formRef.current) return true;
            return formRef.current.trigger();
          },
          syncToPayload: () => {
            const formValues = formRef.current?.getValues();
            payloadRef.current.scorecard = {
              enabled: scorecardEnabled,
              ai_enabled: aiEnabled,
              template_id: selectedTemplate,
              is_feedback_mandatory: formValues?.is_feedback_mandatory ?? false,
              questions: (formValues?.scoreCards ?? []).map((q) => ({
                id: q.id,
                question: q.question,
                type: q.type,
                description: q.description,
                mandatory: q.mandatory,
                options: q.options?.map((o) => o.optionText),
              })),
              feedbackDescription: formValues?.feedback_description ?? '',
              ratingDescription: formValues?.rating_description ?? '',
            };
          },
        }),
        [aiEnabled, payloadRef, scorecardEnabled, selectedTemplate]
      );

      return (
        <Flex
          justifyContent="center"
          w="100%"
          minH={0}
          flex="1"
          overflowY="auto"
          key={String(prefillVersion)}
        >
          <Flex w="720px" maxW="100%" flexDir="column" py="4" pb="2" mb="10">
            <ScorecardStepHeader
              scorecardEnabled={scorecardEnabled}
              onScorecardEnabledChange={setScorecardEnabled}
              aiEnabled={aiEnabled}
              onAiEnabledChange={setAiEnabled}
              showAiToggle={showAiToggle}
              templateOptions={templateOptions}
              selectedTemplate={selectedTemplate}
              onTemplateChange={handleTemplateChange}
              copy={copy}
            />

            {scorecardEnabled && (
              <>
                <AddQuestion formRef={formRef} noteText={copy.templateNote} />
                <ScorecardTemplateBuilder
                  formRef={formRef}
                  initialValues={initialValues}
                />
              </>
            )}
          </Flex>
        </Flex>
      );
    }
  )
);

ScorecardStep.displayName = 'ScorecardStep';
